import { supabase } from '@/integrations/supabase/client';

interface ContactRow {
  id: string;
  name: string | null;
  email?: string | null;
}

/**
 * Fetch contacts by id list and return a `Map<id, ContactRow>` for O(1) merge.
 *
 * Centralises the RLS workaround used in `useConcerts` / `useVenues` where the
 * relational `select('*, contact:contacts(...)')` would hit recursive RLS.
 * We instead fetch the parent rows first, gather contact ids client-side, and
 * resolve them with a single `in(...)` query that goes through the SECURITY
 * DEFINER policy.
 *
 * Returns an empty map when `ids` is empty (no Supabase round-trip).
 */
export async function fetchContactsMap(
  ids: Array<string | null | undefined>,
): Promise<Map<string, ContactRow>> {
  const unique = Array.from(new Set(ids.filter((id): id is string => Boolean(id))));
  if (unique.length === 0) return new Map();

  const { data, error } = await supabase
    .from('contacts')
    .select('id, name, email')
    .in('id', unique);

  if (error) throw error;

  const map = new Map<string, ContactRow>();
  (data ?? []).forEach((row) => map.set(row.id, row as ContactRow));
  return map;
}

/**
 * Generic helper: gather all values found at `fields` across `rows` and resolve
 * them in a single `in()` query against `table` returning `selectColumns`.
 *
 * Useful for any list view that needs to denormalise FK names without
 * triggering relational RLS issues.
 */
export async function fetchRelatedMap<T extends { id: string }>(
  table:
    | 'contacts'
    | 'venues'
    | 'artistic_projects'
    | 'organizations',
  rows: Array<Record<string, unknown>>,
  fields: string[],
  selectColumns = 'id, name',
): Promise<Map<string, T>> {
  const ids: string[] = [];
  for (const row of rows) {
    for (const f of fields) {
      const v = row[f];
      if (typeof v === 'string' && v) ids.push(v);
    }
  }
  const unique = Array.from(new Set(ids));
  if (unique.length === 0) return new Map();

  const { data, error } = await supabase
    .from(table)
    .select(selectColumns)
    .in('id', unique);

  if (error) throw error;

  const map = new Map<string, T>();
  ((data ?? []) as unknown as T[]).forEach((row) => map.set(row.id, row));
  return map;
}
